import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getAllBooks } from '../api/books';
import type { SearchBooksResponse, Book } from '../api/books';
import { useMemo } from 'react';

interface UseAllBooksOptions {
  onSuccess?: (data: SearchBooksResponse) => void;
  onError?: (error: Error) => void;
  enabled?: boolean;
}

export const useAllBooks = (
  category?: string,
  page: number = 1,
  limit: number = 50,
  sortBy?: 'rating' | 'newest',
  options: UseAllBooksOptions = {}
) => {
  return useQuery<SearchBooksResponse, Error>({ 
    queryKey: ['books', 'all', category, page, limit, sortBy],
    queryFn: () => getAllBooks(category, page, limit, sortBy),
    enabled: options.enabled !== false,
    ...options,
  });
};

interface UseAllBooksInfiniteOptions {
  limit?: number;
  sortBy?: 'rating' | 'newest';
  enabled?: boolean;
}

export const useAllBooksInfinite = (
  category?: string,
  options: UseAllBooksInfiniteOptions = {}
) => {
  const { limit = 10, sortBy = 'newest', enabled = true } = options;

  const query = useInfiniteQuery({
    queryKey: ['booksInfinite', category, limit, sortBy],
    queryFn: async ({ pageParam = 1 }) => {
      return getAllBooks(category, pageParam, limit, sortBy);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    enabled,
    staleTime: 30_000,
  });

  const books = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.books || []);
  }, [query.data?.pages]);

  return {
    books,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
  };
};
